import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';

/**
 * Global service that automatically dismisses all tooltips on navigation.
 * This prevents tooltips from persisting after route changes, especially on mobile devices.
 */
@Injectable({
	providedIn: 'root',
})
export class TooltipDismissService implements OnDestroy {
	private routerSubscription?: Subscription;

	constructor(private router: Router) {
		this.setupNavigationListener();
	}

	/**
	 * Sets up a listener for router navigation events.
	 * Automatically dismisses all tooltips when navigation completes.
	 */
	private setupNavigationListener(): void {
		this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
			this.dismissAllTooltips();
		});
	}

	/**
	 * Dismisses all open tooltips in the application.
	 * NOTE: This method can also be called manually if needed.
	 */
	public dismissAllTooltips(): void {
		setTimeout(() => {
			const tooltips = document.querySelectorAll('.tooltip');
			tooltips.forEach((tooltip) => {
				const tooltipElement = tooltip as HTMLElement;
				if (tooltipElement.style.display !== 'none') {
					tooltipElement.remove();
				}
			});
		}, 0);

		// Trigger mouseleave events on all elements with tooltips to ensure tooltip state is updated
		// This includes elements with [eifTooltip] directive
		const elementsWithTooltips = document.querySelectorAll('[eifTooltip], [data-bs-toggle="tooltip"]');
		elementsWithTooltips.forEach((element) => {
			const mouseLeaveEvent = new MouseEvent('mouseleave', {
				bubbles: true,
				cancelable: true,
				view: window,
			});
			element.dispatchEvent(mouseLeaveEvent);
		});
	}

	ngOnDestroy(): void {
		if (this.routerSubscription) {
			this.routerSubscription.unsubscribe();
		}
	}
}
